import { supabase } from './supabase';

export const adminUserService = {
  // Get all users with filtering and pagination
  async getUsers(filters = {}) {
    try {
      let query = supabase
        .from('user_profiles')
        .select(`
          id,
          email,
          full_name,
          role,
          avatar_url,
          status,
          status_reason,
          status_changed_at,
          status_changed_by,
          created_at,
          updated_at,
          subscriptions (
            id,
            plan,
            status,
            current_period_end,
            price_per_month
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.role && filters.role !== 'all') {
        query = query.eq('role', filters.role);
      }

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters.search) {
        query = query.or(`full_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Get user activity data for all users
      const userIds = data.map(user => user.id);
      const activityData = await this.getBulkUserActivity(userIds);

      // Transform data to include computed fields
      const transformedData = data.map(user => {
        const subscription = user.subscriptions?.[0];
        const activity = activityData[user.id] || { total_playtime: 0, games_played: 0 };
        
        // Determine actual subscription status considering trial expiration
        let subscriptionStatus = subscription?.status || 'none';
        let subscriptionDetails = {
          isTrialExpired: false,
          trialEndDate: null,
          daysUntilExpiry: null
        };
        
        if (subscription?.status === 'trialing' && subscription?.current_period_end) {
          const trialEndDate = new Date(subscription.current_period_end);
          const now = new Date();
          const isTrialExpired = trialEndDate < now;
          const daysUntilExpiry = Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24));
          
          subscriptionDetails = {
            isTrialExpired,
            trialEndDate,
            daysUntilExpiry
          };
          
          if (isTrialExpired) {
            subscriptionStatus = 'trial_expired';
          }
        }
        
        return {
          ...user,
          subscription_status: subscriptionStatus,
          subscription_plan: subscription?.plan || 'none',
          subscription_details: subscriptionDetails,
          last_login: user.updated_at, // Using updated_at as proxy for last login
          total_playtime: activity.total_playtime,
          games_played: activity.games_played
        };
      });

      return { data: transformedData, error: null };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { data: [], error: error.message };
    }
  },

  // Get bulk user activity data for multiple users
  async getBulkUserActivity(userIds) {
    try {
      if (userIds.length === 0) return {};

      const { data: sessions, error } = await supabase
        .from('user_game_sessions')
        .select('user_id, duration_minutes, game_id')
        .in('user_id', userIds);

      if (error) throw error;

      // Group by user and calculate stats
      const activityByUser = {};
      
      sessions.forEach(session => {
        if (!activityByUser[session.user_id]) {
          activityByUser[session.user_id] = {
            total_playtime: 0,
            games_played: new Set()
          };
        }
        
        activityByUser[session.user_id].total_playtime += session.duration_minutes || 0;
        activityByUser[session.user_id].games_played.add(session.game_id);
      });

      // Convert sets to counts
      Object.keys(activityByUser).forEach(userId => {
        activityByUser[userId].games_played = activityByUser[userId].games_played.size;
      });

      return activityByUser;
    } catch (error) {
      console.error('Error fetching bulk user activity:', error);
      return {};
    }
  },

  // Get user statistics
  async getUserStats() {
    try {
      const { data: users, error } = await supabase
        .from('user_profiles')
        .select(`
          id,
          role,
          status,
          subscriptions (
            status,
            plan,
            current_period_end
          )
        `);

      if (error) throw error;

      const stats = {
        total: users.length,
        active: users.filter(u => u.status === 'active').length,
        suspended: users.filter(u => u.status === 'suspended').length,
        banned: users.filter(u => u.status === 'banned').length,
        inactive: users.filter(u => u.status === 'inactive').length,
        admins: users.filter(u => u.role === 'admin').length,
        studio_admins: users.filter(u => u.role === 'studio_admin').length,
        premium: users.filter(u => {
          const subscription = u.subscriptions?.[0];
          if (!subscription) return false;
          
          // Check for active subscriptions
          if (subscription.status === 'active') return true;
          
          // Check for active trials (not expired)
          if (subscription.status === 'trialing' && subscription.current_period_end) {
            const trialEndDate = new Date(subscription.current_period_end);
            const now = new Date();
            return trialEndDate > now; // Trial is still active
          }
          
          return false;
        }).length,
        basic: users.filter(u => {
          const subscription = u.subscriptions?.[0];
          return subscription?.plan === 'basic' && subscription?.status === 'active';
        }).length,
        trialing: users.filter(u => {
          const subscription = u.subscriptions?.[0];
          if (subscription?.status === 'trialing' && subscription?.current_period_end) {
            const trialEndDate = new Date(subscription.current_period_end);
            const now = new Date();
            return trialEndDate > now; // Active trials
          }
          return false;
        }).length,
        trial_expired: users.filter(u => {
          const subscription = u.subscriptions?.[0];
          if (subscription?.status === 'trialing' && subscription?.current_period_end) {
            const trialEndDate = new Date(subscription.current_period_end);
            const now = new Date();
            return trialEndDate <= now; // Expired trials
          }
          return false;
        }).length
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return { data: null, error: error.message };
    }
  },

  // Get user activity data (game sessions, playtime)
  async getUserActivity(userId) {
    try {
      const { data: sessions, error } = await supabase
        .from('user_game_sessions')
        .select(`
          id,
          duration_minutes,
          session_start,
          session_end,
          games (
            id,
            title
          )
        `)
        .eq('user_id', userId)
        .order('session_start', { ascending: false });

      if (error) throw error;

      const totalPlaytime = sessions.reduce((sum, session) => sum + (session.duration_minutes || 0), 0);
      const uniqueGames = new Set(sessions.map(s => s.games?.id)).size;

      return {
        data: {
          total_playtime: totalPlaytime,
          games_played: uniqueGames,
          recent_sessions: sessions.slice(0, 5)
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching user activity:', error);
      return { data: null, error: error.message };
    }
  },

  // Create new user
  async createUser(userData) {
    try {
      // First create auth user
      const { data: authData, error: authError } = await supabase.auth.admin.createUser({
        email: userData.email,
        password: userData.password || 'TempPassword123!',
        email_confirm: true,
        user_metadata: {
          full_name: userData.full_name
        }
      });

      if (authError) throw authError;

      // Update user profile with role
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .update({
          role: userData.role,
          full_name: userData.full_name
        })
        .eq('id', authData.user.id)
        .select()
        .single();

      if (profileError) throw profileError;

      return { data: profileData, error: null };
    } catch (error) {
      console.error('Error creating user:', error);
      return { data: null, error: error.message };
    }
  },

  // Update user
  async updateUser(userId, userData) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          full_name: userData.full_name,
          role: userData.role
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating user:', error);
      return { data: null, error: error.message };
    }
  },

  // Update user status (suspend/activate) with reason
  async updateUserStatus(userId, status, reason = null) {
    try {
      const { data, error } = await supabase.rpc('update_user_status', {
        target_user_id: userId,
        new_status: status,
        reason: reason
      });

      if (error) throw error;

      // Send appropriate email based on status change
      if (status === 'suspended' && reason) {
        await this.sendSuspensionEmail(userId, reason);
      } else if (status === 'active') {
        await this.sendReactivationEmail(userId);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating user status:', error);
      return { data: null, error: error.message };
    }
  },

  // Send suspension email
  async sendSuspensionEmail(userId, reason) {
    try {
      // Get user details
      const { data: user, error: userError } = await supabase
        .from('user_profiles')
        .select('email, full_name')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Send email using the email service
      const { error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          template_type: 'account_suspended',
          recipient_email: user.email,
          recipient_name: user.full_name,
          variables: {
            user_name: user.full_name,
            suspension_reason: reason,
            appeal_available: true,
            appeal_deadline: '30',
            appeal_url: `${window.location.origin}/contact?type=appeal&user_id=${userId}`,
            support_url: `${window.location.origin}/contact`,
            terms_url: `${window.location.origin}/terms-of-use`
          }
        }
      });

      if (emailError) {
        console.error('Error sending suspension email:', emailError);
      }
    } catch (error) {
      console.error('Error in sendSuspensionEmail:', error);
    }
  },

  // Send reactivation email
  async sendReactivationEmail(userId) {
    try {
      // Get user details
      const { data: user, error: userError } = await supabase
        .from('user_profiles')
        .select('email, full_name')
        .eq('id', userId)
        .single();

      if (userError) throw userError;

      // Send email using the email service
      const { error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          template_type: 'account_reactivated',
          recipient_email: user.email,
          recipient_name: user.full_name,
          variables: {
            user_name: user.full_name,
            dashboard_url: `${window.location.origin}/library`,
            support_url: `${window.location.origin}/contact`,
            community_url: `${window.location.origin}/community-guidelines`
          }
        }
      });

      if (emailError) {
        console.error('Error sending reactivation email:', emailError);
      }
    } catch (error) {
      console.error('Error in sendReactivationEmail:', error);
    }
  },

  // Delete user
  async deleteUser(userId) {
    try {
      // Delete from auth (this will cascade to user_profiles due to foreign key)
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      
      if (authError) throw authError;

      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { data: null, error: error.message };
    }
  },

  // Bulk operations
  async bulkUpdateUsers(userIds, action) {
    try {
      const results = [];
      
      for (const userId of userIds) {
        let result;
        switch (action) {
          case 'suspend':
            result = await this.updateUserStatus(userId, 'suspended');
            break;
          case 'activate':
            result = await this.updateUserStatus(userId, 'active');
            break;
          case 'delete':
            result = await this.deleteUser(userId);
            break;
          default:
            throw new Error(`Unknown action: ${action}`);
        }
        results.push(result);
      }

      return { data: results, error: null };
    } catch (error) {
      console.error('Error in bulk operation:', error);
      return { data: null, error: error.message };
    }
  },

  // Get user status history
  async getUserStatusHistory(userId) {
    try {
      const { data, error } = await supabase.rpc('get_user_status_history', {
        target_user_id: userId
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user status history:', error);
      return { data: [], error: error.message };
    }
  },

  // Get admin users specifically
  async getAdminUsers() {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          id,
          email,
          full_name,
          role,
          avatar_url,
          created_at,
          updated_at
        `)
        .eq('role', 'admin')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching admin users:', error);
      return { data: [], error: error.message };
    }
  },

  // Promote user to admin
  async promoteToAdmin(userId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ role: 'admin' })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error promoting user to admin:', error);
      return { data: null, error: error.message };
    }
  },

  // Demote admin to user
  async demoteFromAdmin(userId) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ role: 'user' })
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error demoting admin:', error);
      return { data: null, error: error.message };
    }
  }
};
